/**
 * PeppolBadge — badge de statut Peppol pour une facture.
 *
 * Commit 5 (05/10/2026) — Changements :
 *   - Tooltip `acknowledged` neutralisé (plus de "100 % conforme BE 2026").
 *   - Nouveau statut `test_simulated` (badge amber, libellé explicite).
 *   - Bouton "Renvoyer" désactivé si mode non-live sans `allow_mock_send`
 *     ou si `peppol_wk_supplier_id` absent côté tenant.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, RotateCw, Send, TestTube2 } from "lucide-react";

const META = {
  pending: {
    label: "Peppol · En attente",
    cls: "badge-warning",
    tooltip:
      "Envoi Peppol programmé. La facture attend d'être transmise au réseau — cela prend généralement quelques minutes.",
  },
  sent: {
    label: "Peppol · Transmise",
    cls: "badge-info",
    tooltip:
      "Facture transmise au réseau Peppol. Nous attendons l'accusé de réception.",
  },
  acknowledged: {
    label: "Peppol · Accusée",
    cls: "badge-success",
    tooltip: "Facture acceptée par le réseau Peppol.",
  },
  failed: {
    label: "Peppol · Échec",
    cls: "badge-error",
    tooltip:
      "L'envoi Peppol a échoué après plusieurs tentatives. Vérifiez le n° TVA / endpoint Peppol du client puis cliquez sur « Renvoyer ».",
  },
  // Commit 5 — Statut dédié aux envois mock simulés (jamais confondu avec un vrai Peppol).
  test_simulated: {
    label: "SIMULÉ — non transmis",
    cls: "badge-warning",
    tooltip:
      "Mode test Peppol : cette facture n'a PAS été transmise sur le réseau Peppol. Aucune valeur juridique.",
  },
};

export default function PeppolBadge({ status, invoiceId, lastError, onRetried, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);

  // Charge le mode Peppol au premier affichage (une fois par session composant).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/peppol/mode");
        if (active) setMode(r.data);
      } catch {
        if (active) setMode({ mode: "mock", allow_mock_send: false, is_production: false });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!status) return null;
  const meta = META[status] || META.pending;
  const tooltipText = status === "failed" && lastError
    ? `${meta.tooltip} · Détail : ${lastError}`
    : meta.tooltip;

  // Désactivation retry si (a) mode non-live sans mock_send OU (b) prod + mock_send absent.
  const retryDisabled =
    !mode ||
    (mode.mode !== "live" && !mode.allow_mock_send);
  const retryDisabledMsg = retryDisabled
    ? mode?.is_production
      ? "Envoi Peppol en cours d'activation — utilisez votre outil habituel"
      : "Mode test Peppol — PEPPOL_ALLOW_MOCK_SEND requis pour relancer"
    : null;

  const doRetry = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoiceId}/peppol/retry`);
      toast.success("Renvoi Peppol relancé");
      onRetried?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const Icon = status === "test_simulated" ? TestTube2 : Send;

  return (
    <TooltipProvider delayDuration={150}>
      <span className="inline-flex items-center gap-1.5" data-testid={`peppol-badge-${invoiceId}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              className={`border cursor-help ${meta.cls}`}
              data-testid={`peppol-status-${invoiceId}`}
              aria-label={`${meta.label} — ${tooltipText}`}
              title={tooltipText}
            >
              <Icon className="mr-1 h-3 w-3" /> {meta.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent
            className="max-w-xs border-slate-700 bg-slate-800 text-slate-100"
            data-testid={`peppol-tooltip-${invoiceId}`}
          >
            {tooltipText}
          </TooltipContent>
        </Tooltip>
        {status === "failed" && !compact && (
          <Button
            size="sm"
            variant="outline"
            onClick={doRetry}
            disabled={busy || retryDisabled}
            title={retryDisabledMsg || undefined}
            className="h-7 border-slate-700 bg-slate-800/60 px-2 text-xs text-slate-200 hover:bg-slate-800"
            data-testid={`peppol-retry-btn-${invoiceId}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
            <span className="ml-1">Renvoyer</span>
          </Button>
        )}
      </span>
    </TooltipProvider>
  );
}
